import { useEffect, useState } from "react";
import { format, addDays } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { MemberDebt } from "@/hooks/admin/useMemberDebts";
import { useSettleDebt } from "@/hooks/admin/useDebtMutations";
import { usePaymentMethods } from "@/hooks/payments/usePaymentMethods";

interface SettleDebtDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  debt: MemberDebt;
  userName?: string;
}

export const SettleDebtDialog = ({
  open,
  onOpenChange,
  debt,
  userName,
}: SettleDebtDialogProps) => {
  const settle = useSettleDebt();
  const { methods } = usePaymentMethods(debt.gym_id);

  // Default: started_at + category_validity_days
  const defaultExp = format(
    addDays(new Date(debt.started_at), debt.category_validity_days),
    "yyyy-MM-dd"
  );
  const [newExpiration, setNewExpiration] = useState<string>(defaultExp);
  const [methodId, setMethodId] = useState<string>("");

  useEffect(() => {
    if (open) {
      setNewExpiration(defaultExp);
      if (methods.length > 0) setMethodId(methods[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, methods.length]);

  const selectedMethod = methods.find((m) => m.id === methodId);
  const canSubmit = !!newExpiration && !!selectedMethod && !settle.isPending;

  const handleSubmit = async () => {
    if (!selectedMethod) return;
    await settle.mutateAsync({
      debt_id: debt.id,
      user_id: debt.user_id,
      gym_id: debt.gym_id,
      owed_amount: debt.owed_amount,
      category_name: debt.category_name,
      new_expiration_date: newExpiration,
      payment_method: selectedMethod.name,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton className="bg-background">
        <DialogHeader>
          <DialogTitle>Cancelar deuda</DialogTitle>
          <DialogDescription>
            {userName ? `Registrar pago del monto adeudado de ${userName}.` : "Registrar pago del monto adeudado."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Categoría</Label>
            <Input value={debt.category_name} readOnly className="bg-muted" />
          </div>

          <div className="space-y-2">
            <Label>Monto adeudado</Label>
            <Input
              value={`$${debt.owed_amount.toLocaleString()}`}
              readOnly
              className="bg-muted font-semibold"
            />
            <p className="text-xs text-muted-foreground">
              Se registrará un pago por este monto al confirmar.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Nueva fecha de vencimiento de la categoría</Label>
            <DatePicker
              value={newExpiration}
              onChange={setNewExpiration}
              placeholder="Seleccionar fecha"
            />
          </div>

          <div className="space-y-2">
            <Label>Método de pago</Label>
            <Select value={methodId} onValueChange={setMethodId}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar método" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {methods.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {settle.isPending ? "Procesando..." : "Confirmar pago"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
